import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { makeRequest } from "../../../utils/axios.js"; 

export const usePostMediaLogic = (postId) => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
  
    const initialIndex = parseInt(searchParams.get("index") || "0");
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [scale, setScale] = useState(1);

    const { isLoading, error, data } = useQuery({
        queryKey: ["post", postId],
        queryFn: () => makeRequest.get("/posts/" + postId).then((res) => res.data),
    });

    const mediaList = useMemo(() => {
        if (!data) return [];
        const raw = data.originalPostId ? data.originalImg : data.img;
        
        try {
            if (Array.isArray(raw)) return raw;
            if (typeof raw === 'string') {
                return raw.startsWith("[") ? JSON.parse(raw) : [raw];
            }
        } catch (err) {
            console.error("Error parsing media:", err);
            return raw ? [raw] : [];
        }
        return [];
    }, [data]);

    useEffect(() => {
        const indexFromUrl = parseInt(searchParams.get("index") || "0");
        if (indexFromUrl !== currentIndex) {
            setCurrentIndex(indexFromUrl);
        }
    }, [searchParams.get("index")]); 

    useEffect(() => {
        setScale(1);
    }, [currentIndex]);

    const currentFile = mediaList[currentIndex];

    const updateIndex = useCallback((newIndex) => {
        setCurrentIndex(newIndex);
        setSearchParams(
            (prevParams) => {
                prevParams.set("index", newIndex);
                return prevParams;
            }, 
            { replace: true }
        );
    }, [setSearchParams]);

    const handleNext = useCallback(() => {
        if (mediaList.length <= 1) return;
        const nextIndex = currentIndex === mediaList.length - 1 ? 0 : currentIndex + 1;
        updateIndex(nextIndex);
    }, [currentIndex, mediaList.length, updateIndex]);

    const handlePrev = useCallback(() => {
        if (mediaList.length <= 1) return;
        const prevIndex = currentIndex === 0 ? mediaList.length - 1 : currentIndex - 1;
        updateIndex(prevIndex);
    }, [currentIndex, mediaList.length, updateIndex]);

    const handleClose = useCallback(() => navigate(-1), [navigate]);

    const handleZoomIn = () => setScale((prev) => prev + 0.25);
    const handleZoomOut = () => setScale((prev) => (prev > 1 ? prev - 0.25 : 1));
    const handleResetZoom = () => setScale(1);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "ArrowRight") handleNext();
        if (e.key === "ArrowLeft") handlePrev();
        if (e.key === "Escape") handleClose();
    }, [handleNext, handlePrev, handleClose]);

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    return {
        state: {
            isLoading,
            error,
            mediaList,
            currentFile,
            currentIndex,
            scale,
            showNavigation: mediaList.length > 1
        },
        actions: {
            handleClose,
            handleNext,
            handlePrev,
            handleZoomIn,
            handleZoomOut,
            handleResetZoom,
            handleKeyDown
        }
    };
};